<template>
    <div class="ds-date-input">
        <div
            class="ds-date-input-trigger"
            @click="toggle">
            {{ displayValue }}
        </div>

        <div v-if="isOpen" class="ds-date-input-backdrop" @click="isOpen = false" />

        <div v-if="isOpen" class="ds-calendar-popover">
            <AppCalendar
                :model-value="modelValue"
                :mode="mode"
                :precision="precision"
                :disabled-dates="disabledDates"
                :min-date="minDate"
                @update:model-value="onSelect"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import type { Dayjs } from 'dayjs'
import AppCalendar from './AppCalendar.vue'

type Mode = 'single' | 'range' | 'multi'
type Precision = 'day' | 'month'

const props = withDefaults(defineProps<{
    modelValue?: Dayjs | [Dayjs, Dayjs] | Dayjs[] | null
    mode?: Mode
    precision?: Precision
    displayFormat?: string
    disabledDates?: Dayjs[]
    minDate?: Dayjs
    placeholder?: string
}>(), {
    mode: 'single',
    precision: 'day',
    placeholder: 'Выберите дату',
})

const emit = defineEmits<{
    'update:modelValue': [value: Dayjs | [Dayjs, Dayjs] | Dayjs[]]
}>()

const isOpen = ref(false)

const displayValue = computed(() => {
    if (!props.modelValue) return props.placeholder
    if (props.mode === 'single') {
        const value = props.modelValue as Dayjs
        if (props.displayFormat) return value.format(props.displayFormat)
        return props.precision === 'month' ? value.format('MMMM') : value.format('D.M.YYYY')
    }
    if (props.mode === 'range') {
        const [start, end] = props.modelValue as [Dayjs, Dayjs]
        if (!start) return props.placeholder
        if (!end) return start.format('D.M.YYYY') + ' — ...'
        return start.format('D.M.YYYY') + ' — ' + end.format('D.M.YYYY')
    }
    if (props.mode === 'multi') {
        const dates = props.modelValue as Dayjs[]
        if (!dates.length) return props.placeholder
        return dates.map(d => d.format('D.M')).join(', ')
    }
    return props.placeholder
})

function toggle() {
    isOpen.value = !isOpen.value
}

function onSelect(value: Dayjs | [Dayjs, Dayjs] | Dayjs[]) {
    emit('update:modelValue', value)
    // Закрываем popup только при полном выборе
    if (props.mode === 'single') {
        isOpen.value = false
    } else if (props.mode === 'range') {
        const [, end] = value as [Dayjs, Dayjs]
        if (end) isOpen.value = false
    }
    // mode=multi — не закрываем, пользователь сам закрывает
}
</script>
